import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Spinner from 'components/Spinner';
import { getPricingString } from 'common/utils/common';
import { Theme } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { AmountObject } from 'common/types/adyen';
import { CurrencyObject, _PaymentObject } from 'common/types';
import {
	getCurrencyObjectFromCurrencyCode,
	isDisplayAsSymbol,
	getCurrencySymbolFromCurrencyCode,
} from 'common/utils/currencyUtils';
import { useTranslation } from 'services/localization/useTranslation';

interface Refund {
	percentage?: '' | number;
	value: '' | number;
}

interface Props {
	open: boolean;
	paymentObject: _PaymentObject;
	dialogClasses?: any;
	shopCurrency: CurrencyObject;
	onClose: () => void;
	refundLoading: boolean;
	refundError: string;
	handleRefund: (amount: AmountObject) => void;
}

const refundOptions = [
	'',
	0.05,
	0.1,
	0.15,
	0.2,
	0.25,
	0.3,
	0.35,
	0.4,
	0.45,
	0.5,
	0.55,
	0.6,
	0.65,
	0.7,
	0.75,
	0.8,
	0.85,
	0.9,
	0.95,
	1.0,
];

const RefundDialog = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const shopCurrency = props.shopCurrency;
	const { refunded, pendingRefunds, currency, captured, paid } = props.paymentObject;
	const capturedValue = paid || captured || 0;
	const pendingAmount = Object.values(pendingRefunds || {}).reduce((tot, val) => tot + val, 0);
	const maxAmountToRefund = capturedValue - (refunded || 0) - pendingAmount;
	const [refund, setRefund] = useState<Refund>({
		percentage: maxAmountToRefund === capturedValue ? 1.0 : '',
		value: maxAmountToRefund,
	});
	const [refundError, setRefundError] = useState<string>('');
	const nonSufficientAmount = (refund.value || 0) > maxAmountToRefund;

	const handleClose = () => {
		props.onClose();
	};

	useEffect(() => {
		if (props.refundError) {
			setRefundError(props.refundError);
		}
	}, [props.refundError]);

	const handlePercentageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		let percentage: number | '' = parseFloat(event.target.value);
		let refundValue = 0;
		if (isNaN(percentage)) {
			percentage = '';
		} else {
			refundValue = Math.round(capturedValue * percentage);
		}
		if (refundValue && refundValue > maxAmountToRefund) {
			setRefundError(
				t('refundDialog.refundAmountTooHigh', 'The amount cannot be larger than the price.'),
			);
		} else {
			setRefundError('');
		}
		setRefund({
			percentage,
			value: refundValue,
		});
	};

	const handleRefundedAmountChange = (
		event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>,
	) => {
		let value: number | '' = parseFloat(event.target.value) * 100;
		let refundValue: number | '' = '';
		if (isNaN(value)) {
			value = '';
		} else {
			refundValue = value;
		}
		if (refundValue && refundValue > maxAmountToRefund) {
			setRefundError(
				t('refundDialog.refundAmountTooHigh', 'The amount cannot be larger than the price.'),
			);
		} else {
			setRefundError('');
		}
		setRefund({
			value: refundValue,
		});
	};

	const handleRefund = () => {
		props.handleRefund({
			value: refund.value || 0,
			currency: currency,
		});
	};

	return (
		<Dialog
			disableEscapeKeyDown
			maxWidth="xs"
			aria-labelledby="confirmation-dialog-title"
			open={props.open}
			classes={props.dialogClasses}
		>
			<DialogTitle id="confirmation-dialog-title">
				{t('refundDialog.header', 'Refund all or part of the payment')}
			</DialogTitle>
			<DialogContent>
				<Typography variant="body2" gutterBottom>
					{t('refundDialog.originalPrice', 'Charged price')}:{' '}
					{getPricingString(
						capturedValue,
						getCurrencyObjectFromCurrencyCode(currency, shopCurrency),
					)}
				</Typography>
				{Boolean(refunded) && (
					<Typography variant="body2" gutterBottom>
						{t('refundDialog.alreadyRefunded', 'Already refunded')}{' '}
						{getPricingString(
							refunded || 0,
							getCurrencyObjectFromCurrencyCode(currency, shopCurrency),
						)}
					</Typography>
				)}
				{Boolean(pendingAmount) && (
					<Typography variant="caption" color="textSecondary" gutterBottom>
						{t('refundDialog.pending', 'Pending')}:{' '}
						{getPricingString(
							pendingAmount,
							getCurrencyObjectFromCurrencyCode(currency, shopCurrency),
						)}
					</Typography>
				)}
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<TextField
							select
							label={t('refundDialog.refundedPercentage', 'Refunded percentage')}
							name="refund-percentage"
							value={refund.percentage || refund.percentage === 0 ? refund.percentage : ''}
							onChange={handlePercentageChange}
							SelectProps={{
								native: true,
							}}
							margin="normal"
							type="number"
							fullWidth
						>
							{refundOptions.map((option) => (
								<option key={option} value={option}>
									{option && typeof option === 'number' ? Math.round(option * 100) : ''}{' '}
									{option && '%'}
								</option>
							))}
						</TextField>
					</Grid>
					<Grid item xs={12}>
						<TextField
							label={t('refundDialog.refundedAmount', 'Amount to refund')}
							value={refund.value || refund.value === 0 ? refund.value / 100 : ''}
							onChange={handleRefundedAmountChange}
							type="number"
							InputProps={{
								endAdornment: isDisplayAsSymbol(shopCurrency) ? (
									<InputAdornment position="end">
										{getCurrencySymbolFromCurrencyCode(currency)}
									</InputAdornment>
								) : null,
							}}
							fullWidth
						/>
					</Grid>
				</Grid>
				<Grid item xs={12}>
					<Typography variant="body2" color="error" style={{ marginTop: 16 }}>
						{refundError}
					</Typography>
				</Grid>
			</DialogContent>
			<DialogActions className={classes.dialogActions}>
				<Button onClick={handleClose} color="primary" disabled={props.refundLoading}>
					{t('common:actions.close')}
				</Button>
				<Button
					variant="contained"
					color="primary"
					onClick={handleRefund}
					disabled={nonSufficientAmount || !refund.value || props.refundLoading}
					data-qa="refund"
				>
					{props.refundLoading ? <Spinner padding={8} thickness={4} /> : t('common:payment.refund')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		dialogActions: {
			justifyContent: 'center',
			padding: theme.spacing(2),
		},
	}),
);

export default RefundDialog;
